import React, { useEffect, useMemo } from 'react';

import {
  Flex, GridItem, HStack, Stack, Text,
} from '@chakra-ui/react';
import { useSpring, animated } from 'react-spring';
import { PieChart, Pie, Legend } from 'recharts';

import colors from 'theme/colors';
import { getPercentage, handleObjectString } from 'utils/parsers';

interface MaterialCardProps{
    data: GenericObject
    label: string
    color: string
}

interface LabelProps{
    label: string
    color:string
    value: number
}

function Label({ label, color, value }:LabelProps) {
  const AnimatedText = animated(Text);

  const [style, inc] = useSpring(() => ({
    x: 0,
  }));

  useEffect(() => {
    inc({ x: value });
  }, [value]);

  return (
    <HStack alignItems="center">
      <Flex
        borderRadius={100}
        height={3}
        width={3}
        bg={color}
      />
      <Text
        color="gray.500"
        variant="xSmallSemiBold">
        {label}
      </Text>
      <AnimatedText
        color="gray.600"
        variant="xSmallBold">
        {style.x.to((x) => x.toFixed(0))}
      </AnimatedText>
    </HStack>
  );
}

export default function MaterialCard({ data, label, color = 'primary.default' }:MaterialCardProps) {
  const graph = useMemo(() => [
    {
      value: data?.patient,
      fill: handleObjectString(colors, color),
    },
    {
      value: data?.material - data?.patient,
      fill: colors.gray[200],
    },
  ], [data]);

  return (
    <GridItem
      as={Flex}
      bg="white.default"
      color="gray.50"
      flexDir="column"
      borderRadius={8}
      colSpan={2}>
      <Flex
        p={2}
        borderBottom="1px">
        <Text
          color="gray.500"
          variant="xSmallBold">
          {label}
        </Text>
      </Flex>
      <Flex
        width="100%"
        p={4}>
        <Flex width="100%">
          <PieChart
            width={100}
            height={100}>
            <Legend
              content={() => (
                <Flex
                  position="absolute"
                  bottom={6}
                  left={6}
                  align="center"
                  justify="center"
                  height="42px"
                  width="42px">
                  <Text
                    color={color}
                    variant="smallSemiBold">
                    {getPercentage(data?.patient, data?.material).toFixed(0)}%
                  </Text>
                </Flex>
              )}
            />
            <Pie
              data={graph}
              dataKey="value"
              innerRadius={25}
              outerRadius={35}
              legendType="circle"
            />
          </PieChart>
          <Stack alignSelf="center">
            <Label
              label="Materiais:"
              value={data?.material}
              color="gray.200"
            />
            <Label
              label="Pacientes:"
              value={data?.patient}
              color={color}
            />
          </Stack>
        </Flex>
      </Flex>
    </GridItem>
  );
}
